/**
 * Elreign frontend — entry point.
 * Phase 9.1: TanStack Router + dark theme + Supabase client wired.
 * Phase 9 (B1): TanStack Query provider wraps the router so route components
 * can use `useQuery`. Devtools only render in dev.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './lib/auth-context';
import { I18nProvider } from './lib/i18n';
import { DisplayCurrencyProvider } from './lib/currency';
import { queryClient } from './lib/query-client';
import { routeTree } from './routeTree.gen';
import { RootErrorBoundary } from './components/error-pages/root-error-boundary';
import { installClientErrorReporter } from './lib/client-error-reporter';
import { resolveTenantSlugFromHost } from './lib/tenant';
import { applyBrandingToRoot, readCachedBranding } from './lib/branding-runtime';
// Self-hosted webfonts (@fontsource — POPIA-friendly, no external CDN). Repo-wide
// these were only font-family stacks with no actual webfont, so serif/mono silently
// fell back to system fonts. Load the weights the brand + onboarding flow use.
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/newsreader/300.css';
import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/400-italic.css';
import '@fontsource/newsreader/500.css';
// Latin subsets only: the full 400/500.css also pull cyrillic subsets whose
// inlined data-URI faces fail to decode in-browser (console noise on every
// page, caught in the 2026-07-18 sweep) — and no SA surface renders cyrillic.
import '@fontsource/jetbrains-mono/latin-400.css';
import '@fontsource/jetbrains-mono/latin-500.css';
import './index.css';
import './styles/design-system.css';

// Install global error/unhandledrejection capture BEFORE anything renders, so
// boot-time crashes are reported too (→ POST /v1/telemetry/client-error).
installClientErrorReporter();

// White-label flash-prevention: on a tenant subdomain, synchronously re-apply
// the last-known firm colours from the localStorage cache BEFORE React's first
// paint, so a returning visitor never sees an Elreign-teal flash before the
// by-slug fetch resolves. First-ever visit (cold cache) still paints the baked
// default for one fetch frame — unavoidable without SSR. Best-effort: never
// let a branding hiccup block boot.
try {
  const slug = resolveTenantSlugFromHost(
    window.location.host,
    new URLSearchParams(window.location.search),
  );
  if (slug) {
    const cached = readCachedBranding(`slug:${slug}`);
    if (cached) applyBrandingToRoot(document.documentElement, cached);
  }
} catch {
  /* branding is non-critical chrome — never block the app boot on it */
}

const router = createRouter({ routeTree });

// Extend TanStack Router type registry for type-safe navigation.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

// RootErrorBoundary wraps the entire provider tree. The router's __root
// errorComponent only covers the route subtree — a throw in AuthProvider,
// QueryClientProvider, or the router bootstrap (all ABOVE RouterProvider)
// would white-screen without this boundary.
createRoot(rootEl).render(
  <StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* I18n + display-currency providers wrap the app so any surface can
            read the active locale / display currency. Both default to their
            authoritative values (en, ZAR) and add no network call at boot —
            the af catalog is lazy-loaded only when selected. */}
        <I18nProvider>
          <DisplayCurrencyProvider>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </DisplayCurrencyProvider>
        </I18nProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </RootErrorBoundary>
  </StrictMode>,
);
